import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/contexts/I18nContext';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { LegalContentRenderer } from '@/components/LegalContentRenderer';
import { useLegalContent, type LegalDocumentType } from '@/shared/hooks/useLegalContent';

interface LegalDocumentPageProps {
  /** Which markdown document under `public/content/legal/` to render. */
  documentType: LegalDocumentType;
  /** Dotted locale key for the header title, e.g. `terms_of_use.title`. */
  titleKey: string;
  /**
   * Where to go when the browser has no in-app history to go back to.
   * Defaults to the app root.
   */
  backFallbackPath?: string;
}

/**
 * The one page shape shared by every published legal document: standard
 * sub-page header, the markdown fetched by `useLegalContent`, and the shared
 * loading and error branches of `LegalContentRenderer`.
 *
 * The bottom navigation is hidden because these routes are public — a
 * signed-out reader has nothing to navigate to there.
 */
export const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({
  documentType,
  titleKey,
  backFallbackPath = '/',
}) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { content, loading, error } = useLegalContent(documentType);

  /**
   * These documents are linked from outside the app (the wallet setup
   * checkbox, signup, a shared link), so they are often the first entry in the
   * history stack — `navigate(-1)` would leave the app or do nothing. React
   * Router tracks its own position in `history.state.idx`, which is 0 on that
   * first entry.
   */
  const handleBack = () => {
    const routerIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;

    if (routerIndex > 0) {
      navigate(-1);
    } else {
      navigate(backFallbackPath, { replace: true });
    }
  };

  return (
    <SubPageLayout
      title={t(titleKey)}
      onBack={handleBack}
      backLabel={t('common.back')}
      hideBottomNav
    >
      <div className="max-w-4xl mx-auto">
        <LegalContentRenderer content={content} loading={loading} error={error} />
      </div>
    </SubPageLayout>
  );
};
